import type {
  PageState,
  RemoteMessage,
  RemoteToolCall,
  SendMessageOptions,
  ToolCall,
  Turn,
  TurnError,
  TurnErrorKind,
  TurnResult,
} from '../types';
import type { HttpTransport } from './HttpTransport';

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export interface TurnPacing {
  /** How often the job and the transcript are polled. */
  pollMs: number;
  /** A narration segment finishes within this, however long it is. */
  segmentMs: number;
  /** The gap between two streamed groups of tokens. */
  tokenDelayMs: number;
  /** A beat between tool calls, so consecutive steps read as distinct work. */
  stepMs: number;
}

export const DEFAULT_PACING: TurnPacing = {
  pollMs: 400,
  segmentMs: 3500,
  tokenDelayMs: 18,
  stepMs: 350,
};

/** What the transcript of one assistant message has already been delivered as. */
interface Progress {
  text: boolean;
  toolCalls: number;
}

/**
 * One send is one job, and one job is one of these.
 *
 * The protocol has no stream: the client polls the conversation history —
 * scoped to this turn by the user message uid — and the job status beside
 * it. Each assistant message is one model iteration; its text becomes a
 * narration segment paced word by word, its tool calls become events
 * carrying the per-step enrichment the backend records.
 *
 * Delivery is keyed by message uid and tool-call index because the newest
 * message keeps growing tool calls between polls, so every round re-reads
 * from the turn start and only the unseen tail is emitted.
 *
 * The class exists so that all of that is one object with one lifecycle:
 * `cancel()` is a method rather than a closure over six mutable variables,
 * and a subclass can change the pacing or how a tool call is mapped without
 * touching the loop.
 */
export class AgentTurn implements Turn {
  protected cancelled = false;
  protected jobId: string | null = null;
  protected cancelRequested = false;
  protected settled = false;

  protected readonly delivered = new Map<string, Progress>();
  protected segment = -1;
  protected lastToolState: PageState | null = null;
  protected sawBlocked = false;
  protected sawApplied = false;

  constructor(
    protected readonly transport: HttpTransport,
    protected readonly conversationId: string,
    protected readonly options: SendMessageOptions,
    protected readonly pacing: TurnPacing = DEFAULT_PACING,
  ) {}

  cancel(): void {
    this.cancelled = true;
    this.requestCancel();
  }

  protected requestCancel(): void {
    if (!this.jobId || this.cancelRequested) return;
    this.cancelRequested = true;
    this.transport
      .request(`/jobs/${this.jobId}/cancel`, { method: 'POST' })
      .catch(() => {});
  }

  protected finish(result: TurnResult): void {
    if (this.settled) return;
    this.settled = true;
    this.cancelled = true;
    this.options.onDone(result);
  }

  /** Start polling. The job id is known by the caller before this runs. */
  async run(jobId: string, turnStart: string): Promise<void> {
    this.jobId = jobId;
    try {
      for (;;) {
        if (this.cancelled) return;
        const [job, messages] = await Promise.all([
          this.transport.request<{ status: string; error?: string }>(
            `/jobs/${jobId}`,
          ),
          this.fetchTranscript(turnStart),
        ]);
        if (messages) await this.deliver(messages);
        if (this.cancelled) return;

        if (job.status === 'completed') {
          // One final fetch: the last iteration may have landed between the
          // poll and the status flip.
          const rest = await this.fetchTranscript(turnStart);
          if (rest) await this.deliver(rest);
          this.finish({
            newState: this.lastToolState,
            denied: this.sawBlocked && !this.sawApplied,
          });
          return;
        }
        if (job.status === 'failed') {
          this.fail('failed', job.error);
          return;
        }
        if (job.status === 'cancelled') {
          // Cancelled from outside this client — we bail out silently on our
          // own cancel, so this is someone else's doing and worth saying.
          this.fail('cancelled');
          return;
        }
        await sleep(this.pacing.pollMs);
      }
    } catch (error) {
      this.fail(
        (error as { kind?: TurnErrorKind }).kind || 'unreachable',
        (error as Error).message,
      );
    }
  }

  /** A failed transcript read is a skipped round, never a failed turn. */
  protected fetchTranscript(
    turnStart: string,
  ): Promise<RemoteMessage[] | null> {
    return this.transport
      .request<
        RemoteMessage[]
      >(`/conversations/${this.conversationId}/messages?after=${turnStart}`)
      .catch(() => null);
  }

  protected toToolCall(call: RemoteToolCall): ToolCall {
    return {
      name: call.name,
      description: call.description,
      blocked: !!call.blocked,
      failed: !!call.failed,
      blockId: call.block_id || null,
      before: call.before ?? null,
      after: call.after ?? null,
      image: call.image || null,
      state: call.state || null,
    };
  }

  protected async streamText(text: string): Promise<void> {
    this.segment += 1;
    const index = this.segment;
    const tokens = text.split(/(?<=\s)/);
    const group = Math.max(
      1,
      Math.ceil(
        tokens.length / (this.pacing.segmentMs / this.pacing.tokenDelayMs),
      ),
    );
    let streamed = '';
    for (let i = 0; i < tokens.length; i += group) {
      if (this.cancelled) return;
      streamed += tokens.slice(i, i + group).join('');
      this.options.onChunk?.(streamed, index);
      await sleep(this.pacing.tokenDelayMs);
    }
  }

  protected async deliver(messages: RemoteMessage[]): Promise<void> {
    for (const entry of messages) {
      if (this.cancelled) return;
      if (entry.role !== 'assistant') continue;

      let progress = this.delivered.get(entry.uid);
      if (!progress) {
        progress = { text: false, toolCalls: 0 };
        this.delivered.set(entry.uid, progress);
      }
      if (!progress.text && entry.content) {
        progress.text = true;
        await this.streamText(entry.content);
      }

      const toolCalls = entry.tool_calls || [];
      while (progress.toolCalls < toolCalls.length) {
        if (this.cancelled) return;
        const call = toolCalls[progress.toolCalls];
        progress.toolCalls += 1;
        if (call.blocked) this.sawBlocked = true;
        if (call.state) {
          this.lastToolState = call.state;
          this.sawApplied = true;
        }
        this.options.onToolCall(this.toToolCall(call));
        await sleep(this.pacing.stepMs);
      }
    }
  }

  /** Settle the turn as failed. `detail` is for the log, never for the panel. */
  fail(kind: TurnErrorKind, detail?: string): void {
    const error: TurnError = { kind, ...(detail ? { detail } : {}) };
    if (detail) {
      // The reader is told the kind; the detail is for whoever debugs it.
      // eslint-disable-next-line no-console
      console.warn(`The assistant turn failed (${kind}): ${detail}`);
    }
    this.finish({ newState: null, denied: false, error });
  }

  /** Whether a cancel landed before the job id was known. */
  get abandoned(): boolean {
    return this.cancelled;
  }

  adoptJob(jobId: string): void {
    this.jobId = jobId;
    this.requestCancel();
  }
}
